/**
 * The settings page: one destination, an outline down the side, a section in
 * the middle.
 *
 * Settings used to be split in two — a modal hanging off the sidebar for your
 * account, and a seven-tab page behind a link inside it for the school. Both
 * are this page now, and each section has a real URL, so a link into "grade
 * levels" is a link into grade levels rather than a query parameter the page
 * has to interpret on arrival.
 *
 * The rail is desktop-only on purpose. On a narrow screen the overview is the
 * navigation: it already lists every section with what is in it, which is more
 * use than a row of labels.
 */
import { useMemo } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import type { ResultOf } from 'gql.tada';
import type { AppLayoutContext } from '~/types';
import { SettingsRail } from './SettingsRail';
import { attentionSlugs, settingsSummary, type SettingsSummaryRow } from './settingsSummary';
import { useSchoolSettingsQuery, type SchoolSettingsQuery } from './useSchoolSettingsQuery';

type SettingsData = NonNullable<NonNullable<ResultOf<typeof SchoolSettingsQuery>>['school']>;

export interface SettingsOutletContext {
  app: AppLayoutContext;
  /** True while the settings query is in flight with nothing to show yet. */
  loading: boolean;
  error?: string;
  schoolId: string;
  schoolSlug: string;
  schoolName: string;
  isAdmin: boolean;
  data: SettingsData | null;
  refetch: () => void;
  summary: SettingsSummaryRow[];
}

export function SettingsLayout() {
  const app = useOutletContext<AppLayoutContext>();
  const { schoolSlug = '' } = useParams();
  const { school, roleContext, currentUser, isAdmin } = app;

  // Only an administrator has sections that read this, so nobody else pays for it.
  const { data, loading, error, refetch } = useSchoolSettingsQuery(isAdmin ? school?.id : undefined);
  const schoolData = (data?.school ?? null) as SettingsData | null;

  const summary = useMemo(
    () => settingsSummary(schoolData, {
      selectedYearId: roleContext?.selectedSchoolYear?.id,
      userEmail: currentUser?.email,
    }),
    [schoolData, roleContext?.selectedSchoolYear?.id, currentUser?.email]
  );

  const needsAttention = useMemo(() => attentionSlugs(summary), [summary]);

  const context: SettingsOutletContext = {
    app,
    schoolId: school?.id ?? '',
    schoolSlug,
    schoolName: schoolData?.name ?? school?.name ?? 'this school',
    isAdmin,
    loading: loading && !schoolData,
    error: error?.message,
    data: schoolData,
    refetch,
    summary,
  };

  return (
    <div className="settings-surface mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:py-10">
      <div className="lg:flex lg:gap-12">
        {isAdmin && (
          <div className="hidden lg:block">
            <SettingsRail schoolSlug={schoolSlug} needsAttention={needsAttention} isAdmin={isAdmin} />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <Outlet context={context} />
        </div>
      </div>
    </div>
  );
}

export default SettingsLayout;
